import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Brain, Award, TrendingUp, CheckCircle2, Layers } from "lucide-react";
import CanadaFlag from "@/components/CanadaFlag";

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-950 rounded-sm flex items-center justify-center">
              <Brain className="w-4 h-4 text-cyan-400" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-slate-900 text-lg">CogniTrust<span className="text-cyan-600">.</span>AI</div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Platform</a>
            <a href="#testers" className="hover:text-slate-900">For Testers</a>
            <a href="#how" className="hover:text-slate-900">How it works</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="header-login">Sign in</Button></Link>
            <Link to="/register"><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="header-signup">Join the network <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60" />
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full text-xs font-medium mb-5">
              <CanadaFlag className="w-4 h-3 rounded-[1px] shadow-sm" /> Built in Canada · Trusted by global teams
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black text-slate-950 leading-[1.05] tracking-tight">
              Human-in-the-loop<br/>
              <span className="text-cyan-700">QA</span>, built in{" "}
              <span className="inline-flex items-center gap-2 align-baseline">
                Canada
                <CanadaFlag className="inline-block w-8 h-5 rounded-sm shadow-md align-middle" />
              </span>.
            </h1>
            <p className="mt-6 text-slate-600 text-base md:text-lg max-w-lg leading-relaxed">
              Expert testers specializing in <b className="text-slate-900">AI, Payments, API, IoT, AI Agents, Accessibility, and Performance</b> — trusted by AI-native teams.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register"><Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="hero-cta-signup">Join as a tester <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="border-slate-300" data-testid="hero-cta-login">I'm a manager / admin</Button></Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cyan-600"/> 2FA Secured</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cyan-600"/> Role-based</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-cyan-600"/> Quality scored</div>
            </div>
          </div>
          <div className="relative">
            <div className="relative bg-slate-950 rounded-md border border-slate-800 p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Tester Performance</div>
                <span className="pill bg-slate-900 text-cyan-300 border-cyan-700">Elite</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Acceptance", "94.3%"],
                  ["Valid issues", "127"],
                  ["On-time", "98%"],
                  ["Quality", "92"],
                ].map(([k,v]) => (
                  <div key={k} className="bg-slate-900/70 border border-slate-800 rounded-md p-3">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{k}</div>
                    <div className="font-display text-2xl text-white font-bold mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 border-t border-slate-800 pt-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Recent task</div>
                <div className="font-mono text-xs text-cyan-300 mb-1">TSK-2419 · LLM jailbreak — prompt set A</div>
                <div className="text-xs text-slate-400">Submitted → Accepted · payout $120.00</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white border border-slate-200 rounded-md p-3 shadow-lg w-48">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Tester Score</div>
              <div className="flex items-end gap-1 mt-1">
                <span className="font-display text-4xl font-black text-slate-900">94</span>
                <span className="text-sm text-slate-500 mb-1.5">/100</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-cyan-600" style={{width:"94%"}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar — global companies (placeholder wordmarks; swap once real logos are cleared) */}
      <section aria-label="Trusted by global companies" className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="text-center text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-6" data-testid="trust-bar-heading">
            Trusted by global companies
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-8 gap-y-6 items-center opacity-60" data-testid="trust-bar-logos">
            {["Northstar AI","Vantage Pay","IoTronix","OrbitOps","Meridian Health","Kestrel Labs"].map((brand, idx) => (
              <div key={brand} data-testid={`trust-logo-${idx}`} className="flex items-center justify-center">
                <span className="font-display font-bold text-slate-900 text-lg tracking-tight whitespace-nowrap">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-[11px] text-cyan-700 uppercase tracking-wider mb-2">Platform</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-950 max-w-2xl">Everything QA & AI testing teams need — in a single workspace.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { i: Layers, t: "Project & task management", d: "Define scope, assign testers, attach docs, track delivery." },
              { i: Brain, t: "Issue tracking", d: "Severity-scored bug reports with evidence, browser & device metadata." },
              { i: Award, t: "Tester scoring", d: "Auto-calculated quality, on-time, acceptance — Elite to Standard tiers." },
              { i: TrendingUp, t: "Payments & payouts", d: "Track payouts per task. Approve, mark paid, export CSV." },
              { i: ShieldCheck, t: "Email OTP 2FA", d: "Role-based access. Admins and managers must verify with OTP." },
              { i: CheckCircle2, t: "Extensible", d: "Built to plug in client login, AI evals, and auto-matching." },
            ].map((f, idx) => (
              <div key={idx} className="border border-slate-200 rounded-md p-5 hover:border-cyan-300 transition-colors">
                <f.i className="w-5 h-5 text-cyan-700 mb-3" strokeWidth={1.75} />
                <div className="font-display font-bold text-slate-900">{f.t}</div>
                <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500">
          <div>© 2026 CogniTrust AI — Enterprise QA & AI Testing Marketplace</div>
          <div className="font-mono mt-2 md:mt-0">MVP build · AntroTest</div>
        </div>
      </footer>
    </div>
  );
}
